// useTableColumnSizing.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { updateView } from 'services/views/ViewWriteService';

import {
    buildColumnWidthPayload,
    extractViewColumnWidths,
    getAllowedWidthKeys,
    mergeEffectiveColumnWidths,
    normalizeColumnWidths,
} from 'views/table/utils/tableColumnUtils';
import {
    DEFAULT_FIELD_WIDTH,
    DEFAULT_CORNER_WIDTH,
    getMinColumnWidth,
} from 'views/table/hooks/useTableSettings';
import { buildViewSyncPayload } from 'views/table/utils/tableViewSync';
import { canPersistViewLayout } from 'views/utils/organicViews';

const useTableColumnSizing = ({ view, visibleFieldKeys }) => {
    const viewColumnWidths = useMemo(() => (
        extractViewColumnWidths(view?.columnWidths)
    ), [view?.columnWidths]);

    const allowedWidthKeys = useMemo(() => (
        getAllowedWidthKeys(visibleFieldKeys)
    ), [visibleFieldKeys]);

    const normalizedColumnWidths = useMemo(() => (
        normalizeColumnWidths(viewColumnWidths, allowedWidthKeys)
    ), [viewColumnWidths, allowedWidthKeys]);

    const [columnWidthOverrides, setColumnWidthOverrides] = useState({});
    const columnWidthOverridesRef = useRef({});
    const resizeStateRef = useRef(null);

    const effectiveColumnWidths = useMemo(() => (
        mergeEffectiveColumnWidths(normalizedColumnWidths, columnWidthOverrides, allowedWidthKeys)
    ), [normalizedColumnWidths, columnWidthOverrides, allowedWidthKeys]);

    useEffect(() => {
        if (!canPersistViewLayout(view)) return;

        const { payload, widthsChanged } = buildViewSyncPayload({
            viewColumnWidths,
            normalizedColumnWidths,
        });

        if (!widthsChanged) return;

        updateView(view.key, payload).catch((error) => {
            console.error('[TableView] sync column widths error:', error);
        });
    }, [view, viewColumnWidths, normalizedColumnWidths]);

    useEffect(() => {
        setColumnWidthOverrides((prev) => {
            if (!prev || typeof prev !== 'object') return {};
            const next = Object.entries(prev).reduce((acc, [key, value]) => {
                if (allowedWidthKeys.has(key) && typeof value === 'number' && value > 0) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            return next;
        });
    }, [allowedWidthKeys]);

    useEffect(() => {
        columnWidthOverridesRef.current = columnWidthOverrides || {};
    }, [columnWidthOverrides]);

    const handleColumnResizeStart = useCallback((event, key) => {
        if (!key) return;
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = effectiveColumnWidths[key]
            || normalizedColumnWidths[key]
            || (key === 'open' ? DEFAULT_CORNER_WIDTH : DEFAULT_FIELD_WIDTH);
        const minWidth = getMinColumnWidth(key);

        resizeStateRef.current = {
            key,
            startX,
            startWidth,
            minWidth,
        };

        /**
         * handleMouseMove
         *
         * Handles mouse move.
         *
         * @param {unknown} moveEvent - moveEvent.
         */
        const handleMouseMove = (moveEvent) => {
            if (!resizeStateRef.current) return;
            const { key: activeKey, startX, startWidth, minWidth } = resizeStateRef.current;
            const delta = moveEvent.clientX - startX;
            const nextWidth = Math.max(minWidth, Math.round(startWidth + delta));
            resizeStateRef.current.currentWidth = nextWidth;
            setColumnWidthOverrides((prev) => ({
                ...prev,
                [activeKey]: nextWidth,
            }));
        };

        /**
         * handleMouseUp
         *
         * Handles mouse up.
         */
        const handleMouseUp = () => {
            const active = resizeStateRef.current;
            resizeStateRef.current = null;
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            if (!active || !canPersistViewLayout(view)) return;

            const { key: activeKey, startWidth: baseWidth, currentWidth } = active;
            const finalWidth = currentWidth
                ?? effectiveColumnWidths[activeKey]
                ?? baseWidth;

            const latestOverrides = columnWidthOverridesRef.current || {};
            const nextWidths = normalizeColumnWidths({
                ...normalizedColumnWidths,
                ...latestOverrides,
                [activeKey]: finalWidth,
            }, allowedWidthKeys);

            updateView(view.key, buildColumnWidthPayload(nextWidths)).catch((error) => {
                console.error('[TableView] update column width error:', error);
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }, [
        allowedWidthKeys,
        effectiveColumnWidths,
        normalizedColumnWidths,
        view,
    ]);

    return {
        effectiveColumnWidths,
        handleColumnResizeStart,
    };
};

export default useTableColumnSizing;
